class Block {
  constructor(width, height, topLeft, preOccupied = false) {
    this.width = width;
    this.height = height;
    this.topLeft = topLeft;
    this.preOccupied = preOccupied;
    this.occupied = false;
    this.nodeId = null;
  }

  toString() {
    return `[${this.width},${this.height},${this.occupied ? '■' : '□'}]`;
  }
}

export default class BlockLayout {
  /**
   * Col/Row widths default to 1, if colWidths/rowHeights not passed.
   */
  constructor(
    THREE,
    referenceTransform,
    parentTransform,
    rowHeights,
    colWidths,
    options
  ) {
    this.THREE = THREE;
    this._initBlockLayoutData(
      referenceTransform,
      parentTransform,
      rowHeights,
      colWidths,
      options
    );
    this.blocks = this._createBlocks();
  }

  _initBlockLayoutData(
    referenceTransform,
    parentTransform,
    rowHeights,
    colWidths,
    options = {}
  ) {
    const {
      occupyRow,
      occupyCol,
      withRotation,
      marginLeft = Infinity,
      marginRight = Infinity,
      marginTop = Infinity,
      marginBottom = Infinity,
    } = options;
    const { Matrix4, Euler } = this.THREE;
    this.transform = referenceTransform || new Matrix4();
    this.inverseTransform = new Matrix4().getInverse(this.transform);
    this.rotation = new Euler()
      .setFromRotationMatrix(this.transform, 'ZYX')
      .toVector3()
      .multiplyScalar(180 / Math.PI);

    this.parentTransform = parentTransform || new Matrix4();
    this.inverseParentTransform = new Matrix4().getInverse(
      this.parentTransform
    );

    this.colWidths = colWidths || [1];
    this.colTranslation = [];
    this.totalWidth = this.colWidths.reduce((sum, width, idx) => {
      this.colTranslation[idx] = sum;
      return sum + width;
    }, 0);

    this.rowHeights = rowHeights || [1];
    this.rowTranslation = [];
    this.totalHeight = this.rowHeights.reduce((sum, height, idx) => {
      this.rowTranslation[idx] = sum;
      return sum + height;
    }, 0);

    this.marginLeft = marginLeft;
    this.marginRight = marginRight;
    this.marginTop = marginTop;
    this.marginBottom = marginBottom;

    this.withRotation = withRotation;
    this.occupyRow = occupyRow;
    this.occupyCol = occupyCol;
    this.models = {};
  }

  //Constructor helper
  _createBlocks() {
    const blocks = [];
    let width = 1;
    let height = 1;
    let top = 0;
    let left = 0;
    for (let row = 0; row < this.rowTranslation.length; row++) {
      blocks.push([]);
      left = 0;
      height = this.rowHeights[row];
      for (let col = 0; col < this.colTranslation.length; col++) {
        width = this.colWidths[col];
        const preOccupy =
          this.occupyRow &&
          this.occupyRow[row] &&
          this.occupyCol &&
          this.occupyCol[col];
        const topLeft = { x: left, y: top };
        blocks[row][col] = new Block(width, height, topLeft, preOccupy);
        left += width;
      }
      top += height;
    }
    return blocks;
  }

  /**
   * Update the transform of the blocks, should be called when the mesh that
   * the blocks associated to change transform
   * @param {Matrix4} referenceTransform
   * @param {Matrix4} parentTransform
   */
  updateTransform(referenceTransform, parentTransform, callBack) {
    if (referenceTransform) {
      this.transform = referenceTransform;
      this.inverseTransform.getInverse(referenceTransform);
    }
    if (parentTransform) {
      this.parentTransform = parentTransform;
      this.inverseParentTransform.getInverse(parentTransform);
    }
    if (callBack) {
      const models = Object.entries(this.models).map(
        ([modelId, { rowStart, colStart }]) => ({
          modelId,
          translation: this._convertToParentSpace({
            x: this.colTranslation[colStart],
            y: this.rowTranslation[rowStart],
          }),
        })
      );
      callBack(models);
    }
  }

  /**
   *
   * @param {*} rowHeights array of row heights to add to the bottom of the block
   * @param {*} callBack a function to be called after block got updated
   */
  addRows(rowHeights, callBack) {
    let top = this.totalHeight;
    let left = 0;
    for (let rowIdx = 0; rowIdx < rowHeights.length; ++rowIdx) {
      this.blocks.push([]);
      left = 0;
      for (let col = 0; col < this.colTranslation.length; ++col) {
        const width = this.blocks[this.rowTranslation.length - 1][col].width;
        const height = rowHeights[rowIdx];
        this.blocks[this.rowTranslation.length][col] = new Block(
          width,
          height,
          {
            x: left,
            y: top,
          }
        );
        left += width;
      }
      this.rowTranslation.push(top);
      top += rowHeights[rowIdx];
      this.totalHeight += rowHeights[rowIdx];
    }
    this.rowHeights = this.rowHeights.concat(rowHeights);
    callBack && callBack();
  }

  /**
   *
   * @param {*} rowNum number of row to be deleted from the bottom
   * @param {*} callBack a function to be called after block got updated, a array of existing model will be passed as the argument
   */
  deleteRows(rowNum, callBack) {
    const models = Object.entries(this.models).map(([modelId, insertion]) => {
      const { rowEnd, nodeInfo } = insertion;
      const remainRowNum = this.blocks.length - rowNum;
      const remainMarginBottom =
        this.rowTranslation[remainRowNum] -
        this.rowTranslation[rowEnd] +
        this.marginBottom;
      const valid =
        rowEnd <= remainRowNum &&
        (nodeInfo.marginBottom
          ? remainMarginBottom >= nodeInfo.marginBottom
          : true);

      if (!valid) this.unoccupyBlocks(modelId);

      return { modelId, valid };
    });
    this.totalHeight = this.rowTranslation[this.rowTranslation.length - rowNum];
    this.blocks.splice(this.rowTranslation.length - rowNum, rowNum);
    this.rowTranslation.splice(this.rowTranslation.length - rowNum, rowNum);
    this.rowHeights.splice(this.rowHeights.length - rowNum, rowNum);
    callBack && callBack(models);
  }

  /**
   *
   * @param {*} margin optional margin object with the new margin values, default is Infinity
   * @param {*} callBack optional callback function, takes an array that include all current modelId and it valid status after update margin,
   */

  updateMargin(margin = {}, callBack) {
    const {
      marginLeft = Infinity,
      marginRight = Infinity,
      marginTop = Infinity,
      marginBottom = Infinity,
    } = margin;
    this.marginLeft = marginLeft;
    this.marginRight = marginRight;
    this.marginTop = marginTop;
    this.marginBottom = marginBottom;
    if (callBack) {
      const models = Object.entries(this.models).map(
        ([modelId, { rowStart, colStart, nodeInfo }]) => {
          const { isInsertionPossible } = this._checkInsertionAtLocation(
            nodeInfo,
            rowStart,
            colStart,
            modelId
          );
          return {
            modelId,
            valid: isInsertionPossible,
          };
        }
      );
      callBack(models);
    }
  }

  forceInsert(nodeInfo, row, col, callback) {
    const ignoreNodes = Object.keys(this.models);
    const insertion = this._checkInsertionAtLocation(
      nodeInfo,
      row,
      col,
      ignoreNodes
    );

    if (insertion.isInsertionPossible && callback) {
      const models = Object.entries(this.models).map(
        ([modelId, modelInsertion]) => {
          const valid =
            modelInsertion.colStartWithMargin > insertion.colEndWithMargin ||
            modelInsertion.rowStartWithMargin > insertion.rowEndWithMargin ||
            modelInsertion.colEndWithMargin < insertion.colStartWithMargin ||
            modelInsertion.rowEndWithMargin < insertion.rowStartWithMargin;
          return { modelId, valid };
        }
      );
      callback(models);
    }
    return insertion;
  }

  /**
   * Determines if a node with nodeInfo can be inserted with insertionConstraints.
   * Return an object with insertion information that can be passed to insert(..) to perform the insertion.
   * The returned object MUST have an isInsertionPossible property.
   * @param nodeInfo - object with width and height properties, and optional marginTop, marginBottom, marginLeft, marginRight
   * @param insertionConstraints - (optional)
   * @returns {{isInsertionPossible, rowStart, rowEnd, colStart, colEnd, fitWidth, fitHeight}}
   */
  determineInsertion(nodeInfo, insertionConstraints = {}) {
    let { row, col, nodeIdToIgnore, overlayCheck } = insertionConstraints;
    const checkAtLocation = row !== undefined && col !== undefined;
    const options = { nodeIdToIgnore, overlayCheck };
    return checkAtLocation
      ? this._checkInsertionAtLocation(nodeInfo, row, col, options)
      : this._findInsertions(nodeInfo, { ...options, findFirst: true });
  }

  _checkInsertionAtLocation(nodeInfo, row, col, options = {}) {
    const { ignorePreOccupy } = nodeInfo;
    const insertion = { isInsertionPossible: false };
    const insertArea = this._getInsertArea(nodeInfo, row, col);
    if (!insertArea) return insertion;

    //check if the needed blocks are occupied
    if (this._checkInsertArea(insertArea, { ...options, ignorePreOccupy }))
      return insertion;

    return Object.assign(insertion, insertArea, {
      isInsertionPossible: true,
      nodeInfo,
    });
  }

  _findInsertions(nodeInfo, options = {}) {
    const { ignorePreOccupy } = nodeInfo;
    const { nodeIdToIgnore, overlayCheck, findFirst } = options;
    const self = this;
    function findInsertionInArea(area) {
      const { rowStart, colStart, rowEnd, colEnd } = area;
      if (rowStart > rowEnd || colStart > colEnd) return;
      if (insertion.isInsertionPossible && findFirst) return;

      const insertArea = self._getInsertArea(nodeInfo, rowStart, colStart);
      if (!insertArea) return;
      const invalidBlock = self._checkInsertArea(insertArea, {
        nodeIdToIgnore,
        ignorePreOccupy,
        overlayCheck,
      });
      let firstSubArea, secondSubArea;
      if (!invalidBlock) {
        if (!insertion.isInsertionPossible)
          Object.assign(insertion, insertArea, {
            isInsertionPossible: true,
            nodeInfo,
          });

        if (!findFirst) {
          applyInsertMap(insertArea);
          firstSubArea = {
            rowStart: rowStart + 1,
            colStart,
            rowEnd,
            colEnd: colStart,
          };
          secondSubArea = { rowStart, colStart: colStart + 1, rowEnd, colEnd };
        }
      } else {
        const { row, col } = invalidBlock;
        firstSubArea = { rowStart: row + 1, colStart, rowEnd, colEnd: col };
        secondSubArea = { rowStart, colStart: col + 1, rowEnd, colEnd };
      }
      firstSubArea && findInsertionInArea(firstSubArea);
      secondSubArea && findInsertionInArea(secondSubArea);
    }
    function applyInsertMap(insertArea) {
      const { rowStart, colStart } = insertArea;
      if (!insertion.insertMap) insertion.insertMap = {};
      if (!insertion.insertMap[rowStart]) insertion.insertMap[rowStart] = {};
      insertion.insertMap[rowStart][colStart] = insertArea;
    }

    const insertion = { isInsertionPossible: false };
    const {
      width,
      height,
      marginLeft,
      marginRight,
      marginBottom,
      marginTop,
    } = nodeInfo;

    // check width
    if (width > this.totalWidth) return insertion;
    if (marginLeft && this.marginLeft + this.totalWidth < width + marginLeft)
      return insertion;
    if (marginRight && this.marginRight + this.totalWidth < width + marginRight)
      return insertion;
    if (
      marginLeft &&
      marginRight &&
      this.marginRight + this.marginLeft + this.totalWidth <
        width + marginLeft + marginRight
    )
      return insertion;

    // check height
    if (height > this.totalHeight) return insertion;
    if (marginTop && this.marginTop + this.totalHeight < height + marginTop)
      return insertion;
    if (
      marginBottom &&
      this.marginBottom + this.totalHeight < height + marginBottom
    )
      return insertion;
    if (
      marginTop &&
      marginBottom &&
      this.marginTop + this.marginBottom + this.totalHeight <
        height + marginTop + marginBottom
    )
      return insertion;

    const rowNum = this.rowTranslation.length;
    const colNum = this.colTranslation.length;
    const startWithMargin = { rowStartWithMargin: 0, colStartWithMargin: 0 };
    const endWithMargin = {
      rowEndWithMargin: rowNum - 1,
      colEndWithMargin: colNum - 1,
    };

    const start = this._convertStartWithMarginToStart(
      startWithMargin,
      nodeInfo
    );
    const end = this._convertEndWithMarginToEnd(endWithMargin, nodeInfo);
    findInsertionInArea({ ...start, ...end });
    return insertion;
  }

  _checkInsertionPossible(block, nodeIdToIgnore, ignorePreOccupy) {
    let isBlockOccipied = block.occupied;
    if (isBlockOccipied && nodeIdToIgnore)
      isBlockOccipied = !(Array.isArray(nodeIdToIgnore)
        ? nodeIdToIgnore.includes(block.nodeId)
        : block.nodeId === nodeIdToIgnore);
    const isBlockPreOccupied = !ignorePreOccupy && block.preOccupied;
    return !(isBlockOccipied || isBlockPreOccupied);
  }

  _getInsertArea(nodeInfo, row, col) {
    const { width, height } = nodeInfo;
    const rowNum = this.rowTranslation.length;
    const colNum = this.colTranslation.length;

    let rowEnd = row;
    let colEnd = col;
    let block = this.blocks[row][col];
    let widthSoFar = block.width;
    let heightSoFar = block.height;

    //find how many cols the node needs
    while (width > widthSoFar && colEnd < colNum - 1)
      widthSoFar += this.blocks[row][++colEnd].width;
    if (width > widthSoFar) return;

    //find how many rows the node needs
    while (height > heightSoFar && rowEnd < rowNum - 1)
      heightSoFar += this.blocks[++rowEnd][col].height;
    if (height > heightSoFar) return;

    const start = { rowStart: row, colStart: col };
    const startWithMargin = this._convertStartToStartWithMargin(
      start,
      nodeInfo
    );
    if (!startWithMargin) return;

    const end = { rowEnd, colEnd };
    const endWithMargin = this._convertEndToEndWithMargin(end, nodeInfo);
    if (!endWithMargin) return;

    return Object.assign(start, end, startWithMargin, endWithMargin, {
      nodeInfo,
    });
  }

  _checkInsertArea(insertArea, options = {}) {
    const {
      rowStart,
      rowEnd,
      rowStartWithMargin,
      rowEndWithMargin,
      colStart,
      colEnd,
      colStartWithMargin,
      colEndWithMargin,
      nodeInfo,
    } = insertArea;
    const { nodeIdToIgnore, ignorePreOccupy, overlayCheck } = options;
    let valid = true;
    let row = rowEndWithMargin;
    let col = colEndWithMargin;

    for (; row >= rowStartWithMargin && valid; --row) {
      col = colEndWithMargin;
      for (; col >= colStartWithMargin; --col) {
        const couldIgnorePreOccipied =
          ignorePreOccupy ||
          ((col >= colStartWithMargin && col < colStart) ||
            (col > colEnd && col <= colEndWithMargin) ||
            ((row >= rowStartWithMargin && row < rowStart) ||
              (row > rowEnd && row <= rowEndWithMargin)));

        const insertPossible = this._checkInsertionPossible(
          this.blocks[row][col],
          nodeIdToIgnore,
          couldIgnorePreOccipied
        );
        if (!insertPossible) {
          valid = false;
          break;
        }
      }
    }

    if (overlayCheck) {
      const thisItemTranslation = this._convertToWorldSpace(
        this.blocks[rowStart][colStart].topLeft
      );
      const axis = overlayCheck[0].axis;
      const thisItemStart = thisItemTranslation[axis];
      const thisItemStartWithMargin =
        thisItemStart -
        (axis === 'x' ? nodeInfo.marginLeft : nodeInfo.marginTop);
      const thisItemEnd =
        thisItemStart + (axis === 'x' ? nodeInfo.width : nodeInfo.height);
      const thisItemEndWithMargin =
        thisItemEnd +
        (axis === 'x' ? nodeInfo.marginRight : nodeInfo.marginBottom);

      const targetTranslation = new this.THREE.Vector3();
      for (
        let overlayIdx = 0;
        overlayIdx < overlayCheck.length && valid;
        ++overlayIdx
      ) {
        const { block, items } = overlayCheck[overlayIdx];
        for (let itemIdx = 0; itemIdx < items.length && valid; ++itemIdx) {
          const item = block.getNodeInsertion(items[itemIdx]);
          block._convertToWorldSpace(
            block.blocks[item.rowStart][item.colStart].topLeft,
            targetTranslation
          );
          const targetItemStart = targetTranslation[axis];

          const targetItemStartWithMargin =
            targetItemStart -
            (axis === 'x' ? item.nodeInfo.marginLeft : item.nodeInfo.marginTop);
          const targetItemEnd =
            targetItemStart +
            (axis === 'x' ? item.nodeInfo.width : item.nodeInfo.height);
          const targetItemEndWithMargin =
            targetItemEnd +
            (axis === 'x'
              ? item.nodeInfo.marginRight
              : item.nodeInfo.marginBottom);

          valid =
            (thisItemStart >= targetItemEndWithMargin &&
              thisItemStartWithMargin >= targetItemEnd) ||
            (thisItemEndWithMargin <= targetItemStart &&
              thisItemEnd <= targetItemStartWithMargin);
        }
      }
    }

    if (!valid)
      return { row: Math.max(rowStart, row + 1), col: Math.max(colStart, col) };
  }

  _convertStartToStartWithMargin(start, nodeInfo) {
    const { marginTop, marginLeft } = nodeInfo;
    const { rowStart, colStart } = start;
    let rowStartWithMargin = rowStart;
    let colStartWithMargin = colStart;
    let sideMargin = 0;

    while (sideMargin < marginTop && rowStartWithMargin > 0)
      sideMargin += this.blocks[--rowStartWithMargin][colStart].height;
    if (rowStartWithMargin === 0 && this.marginTop < marginTop - sideMargin)
      return;

    sideMargin = 0;
    while (sideMargin < marginLeft && colStartWithMargin > 0)
      sideMargin += this.blocks[rowStart][--colStartWithMargin].width;
    if (colStartWithMargin === 0 && this.marginLeft < marginLeft - sideMargin)
      return;

    return { rowStartWithMargin, colStartWithMargin };
  }

  _convertEndToEndWithMargin(end, nodeInfo) {
    const { marginBottom, marginRight } = nodeInfo;
    const { rowEnd, colEnd } = end;
    const rowNum = this.rowTranslation.length;
    const colNum = this.colTranslation.length;

    let rowEndWithMargin = rowEnd;
    let colEndWithMargin = colEnd;

    let sideMargin = 0;
    while (sideMargin < marginBottom && rowEndWithMargin < rowNum - 1)
      sideMargin += this.blocks[++rowEndWithMargin][colEnd].height;
    if (
      rowEndWithMargin === rowNum - 1 &&
      this.marginBottom < marginBottom - sideMargin
    )
      return;

    sideMargin = 0;
    while (sideMargin < marginRight && colEndWithMargin < colNum - 1)
      sideMargin += this.blocks[rowEnd][++colEndWithMargin].width;
    if (
      colEndWithMargin === colNum - 1 &&
      this.marginRight < marginRight - sideMargin
    )
      return;

    return { rowEndWithMargin, colEndWithMargin };
  }

  _convertStartWithMarginToStart(startWithMargin, nodeInfo) {
    const { marginTop, marginLeft } = nodeInfo;
    const { rowStartWithMargin, colStartWithMargin } = startWithMargin;
    const rowNum = this.rowTranslation.length;
    const colNum = this.colTranslation.length;

    let rowStart = rowStartWithMargin;
    let colStart = colStartWithMargin;

    let remainMargin = rowStartWithMargin
      ? marginTop
      : marginTop - this.marginTop;
    while (remainMargin > 0 && rowStart < rowNum)
      remainMargin -= this.blocks[rowStart++][colStartWithMargin].height;
    if (remainMargin > 0) return;

    remainMargin = colStartWithMargin
      ? marginLeft
      : marginLeft - this.marginLeft;
    while (remainMargin > 0 && colStart < colNum)
      remainMargin -= this.blocks[rowStartWithMargin][colStart++].width;
    if (remainMargin > 0) return;
    return { rowStart, colStart };
  }

  _convertEndWithMarginToEnd(endWithMargin, nodeInfo) {
    const { marginBottom, marginRight } = nodeInfo;
    const { rowEndWithMargin, colEndWithMargin } = endWithMargin;
    const rowNum = this.rowTranslation.length;
    const colNum = this.colTranslation.length;
    let rowEnd = rowEndWithMargin;
    let colEnd = colEndWithMargin;

    let remainMargin =
      rowEndWithMargin === rowNum - 1
        ? marginBottom - this.marginBottom
        : marginBottom;
    while (remainMargin > 0 && rowEnd >= 0)
      remainMargin -= this.blocks[rowEnd--][colEndWithMargin].height;
    if (remainMargin > 0) return;

    remainMargin =
      colEndWithMargin === colNum - 1
        ? marginRight - this.marginRight
        : marginRight;
    while (remainMargin > 0 && colEnd >= 0)
      remainMargin -= this.blocks[rowEndWithMargin][colEnd--].width;
    if (remainMargin > 0) return;
    return { rowEnd, colEnd };
  }

  /**
   *
   * @param {*} insertion
   * convert a insertion object and return the translation and rotation in the parent space
   */
  getInsertTransform(insertion) {
    if (!insertion) return;
    const { rowStart, colStart } = insertion;
    const block = this.blocks[rowStart][colStart];
    const translation = this._convertToParentSpace(block.topLeft);
    const rotation = new this.THREE.Vector3();
    this.withRotation && rotation.copy(this.rotation);
    return { translation, rotation };
  }

  /**
   *
   * @param {*} nodeId
   * return the insertion of a given model
   */
  getNodeInsertion(nodeId) {
    return this.models[nodeId];
  }

  /**
   *
   * @param {*} modelId
   * @param {*} nodeInfo
   * @param {*} onMove
   * @param {*} onEnd
   *
   */

  getCommandOptions(modelId, nodeInfo, movementOptions = {}) {
    const {
      searchRange = 0,
      onStart,
      onMove,
      onEnd,
      overlayCheck,
    } = movementOptions;
    const plane = new this.THREE.Plane().setFromCoplanarPoints(
      new this.THREE.Vector3(0, 0, 0).applyMatrix4(this.transform),
      new this.THREE.Vector3(1, 0, 0).applyMatrix4(this.transform),
      new this.THREE.Vector3(0, 1, 0).applyMatrix4(this.transform)
    );
    const translation = new this.THREE.Vector3();
    let lastInsertion = this.models[modelId];
    this.unoccupyBlocks(modelId);
    const insertMap = this._findInsertions(nodeInfo, { overlayCheck })
      .insertMap;

    onStart && onStart();
    const shouldMove = (ev, newPosi) => {
      this._convertToLayoutSpace(newPosi, translation);
      const blockPosition = this._findClosestValidBlock(
        translation,
        nodeInfo,
        insertMap,
        ev,
        searchRange
      );
      if (!blockPosition) return;
      const { row, col } = blockPosition;
      const insertArea = insertMap[row][col];

      if (onMove && !onMove(insertArea)) return false;
      lastInsertion = insertArea;
      const blockTopLeft = this.blocks[row][col].topLeft;
      this._convertToWorldSpace(blockTopLeft, translation);
      newPosi.x = translation.x;
      newPosi.y = translation.y;
      newPosi.z = translation.z;
      return true;
    };

    const commandOnEnd = () => {
      this.occupyBlocks(modelId, { ...lastInsertion, nodeInfo });
      onEnd && onEnd(lastInsertion);
    };
    const options = {
      keepOffset: true,
      displayGizmo: false,
      mode: 'plane',
      plane,
      shouldMove,
      onEnd: commandOnEnd,
    };
    return options;
  }

  // if mouse position is outside the grid
  // the code will find the available block that is most near to the mouse position
  _findClosestValidBlock(
    blockTranslation,
    nodeInfo,
    insertMap,
    ev,
    searchRange
  ) {
    const self = this;
    function binarySearch(array, value, optionalBegin, optionalEnd) {
      const begin = optionalBegin || 0;
      const end = optionalEnd || array.length - 1;
      if (end - begin < 2)
        return Math.abs(array[begin] - value) < Math.abs(array[end] - value)
          ? begin
          : end;
      const middle = Math.floor((begin + end) / 2);
      return array[middle] > value
        ? binarySearch(array, value, begin, middle)
        : binarySearch(array, value, middle, end);
    }

    // search a given row for the first available block
    // the search start from a given col, based on a given direction and searchRange
    // return the col number for the first valid block, otherwise, return undefined
    function searchBlockRow(row, col, direction, searchRange) {
      let checkedCol = col;
      let distance = 0;
      while (
        checkedCol >= 0 &&
        checkedCol < self.colTranslation.length &&
        distance <= searchRange
      ) {
        if (insertMap[row] && insertMap[row][checkedCol]) return checkedCol;
        checkedCol += direction;
        distance = Math.abs(
          self.colTranslation[checkedCol] - self.colTranslation[col]
        );
      }
    }

    // search a given col for the first available block
    // the search start from a given row, based on a given direction and searchRange
    // return the row number for the first valid block, otherwise, return undefined
    function searchBlockCol(row, col, direction, searchRange) {
      let checkedRow = row;
      let distance = 0;
      while (
        checkedRow >= 0 &&
        checkedRow < self.rowTranslation.length &&
        distance <= searchRange
      ) {
        if (insertMap[checkedRow] && insertMap[checkedRow][col])
          return checkedRow;
        checkedRow += direction;
        distance = Math.abs(
          self.rowTranslation[checkedRow] - self.rowTranslation[row]
        );
      }
    }

    const { x, y } = blockTranslation;
    let row, col, rowRange, colRange;
    const rowNum = this.rowTranslation.length;

    // define the initial row,col number and
    // and mouse position relative to the block
    // -1/1 means the mouse is outside the block area for corresponding axis
    if (y < this.rowTranslation[0]) {
      row = 0;
      rowRange = -1;
    } else if (y > this.totalHeight) {
      row = rowNum - 1;
      rowRange = 1;
    } else {
      row = binarySearch(this.rowTranslation, y);
      rowRange = 0;
    }

    // if the row number result's the remaining total row height is less than model height (certainly in this case the model will not be able to insert)
    // then return the row number until the remaining total row height is enough for the model
    while (
      this.totalHeight - this.rowTranslation[row] < nodeInfo.height &&
      row > 0
    )
      --row;

    const colNum = this.colTranslation.length;
    if (x < this.colTranslation[0]) {
      col = 0;
      colRange = -1;
    } else if (x > this.totalWidth) {
      col = colNum - 1;
      colRange = 1;
    } else {
      col = binarySearch(this.colTranslation, x);
      colRange = 0;
    }

    while (
      this.totalWidth - this.colTranslation[col] < nodeInfo.width &&
      col > 0
    )
      --col;

    if (insertMap[row] && insertMap[row][col]) return { row, col };

    // search the row/col based on mouse move direction
    if (Math.abs(ev.deltaX) > Math.abs(ev.deltaY)) {
      const direction = colRange ? -colRange : ev.deltaX >= 0 ? 1 : -1;
      const distance = colRange ? Infinity : searchRange;
      col = searchBlockRow(row, col, direction, distance);
    } else {
      const direction = rowRange ? -rowRange : ev.deltaY >= 0 ? 1 : -1;
      const distance = rowRange ? Infinity : searchRange;
      row = searchBlockCol(row, col, direction, distance);
    }

    if (row !== undefined && col !== undefined) return { row, col };
  }

  _convertToWorldSpace(blockTopleft, optionalTarget) {
    optionalTarget = optionalTarget || new this.THREE.Vector3();
    optionalTarget.x = blockTopleft.x;
    optionalTarget.y = -blockTopleft.y;
    optionalTarget.z = 0;
    optionalTarget.applyMatrix4(this.transform);
    return optionalTarget;
  }

  _convertToParentSpace(blockTopLeft, optionalTarget) {
    optionalTarget = optionalTarget || new this.THREE.Vector3();
    this._convertToWorldSpace(blockTopLeft, optionalTarget);
    optionalTarget.applyMatrix4(this.inverseParentTransform);
    return optionalTarget;
  }

  _convertToLayoutSpace(pos, optionalTarget) {
    optionalTarget = optionalTarget || new this.THREE.Vector3();
    optionalTarget.copy(pos).applyMatrix4(this.inverseTransform);
    optionalTarget.y = -optionalTarget.y;
    return optionalTarget;
  }

  /**
   *
   * @param {*} nodeId
   * @param {*} insertion
   * occupy the blocks defined in the insertion object
   */
  occupyBlocks(nodeId, insertion) {
    const {
      colStartWithMargin,
      colEndWithMargin,
      rowStartWithMargin,
      rowEndWithMargin,
    } = insertion;
    for (let i = rowStartWithMargin; i <= rowEndWithMargin; ++i) {
      for (let j = colStartWithMargin; j <= colEndWithMargin; j++) {
        this.blocks[i][j].occupied = true;
        this.blocks[i][j].nodeId = nodeId;
      }
    }
    this.models[nodeId] = { ...insertion };
  }

  unoccupyBlocks(nodeId) {
    const {
      colStartWithMargin,
      colEndWithMargin,
      rowStartWithMargin,
      rowEndWithMargin,
    } = this.models[nodeId];
    for (let i = rowStartWithMargin; i <= rowEndWithMargin; ++i) {
      for (let j = colStartWithMargin; j <= colEndWithMargin; j++) {
        this.blocks[i][j].occupied = false;
        this.blocks[i][j].nodeId = null;
      }
    }
    delete this.models[nodeId];
  }

  containNode(nodeId) {
    return !!this.models[nodeId];
  }

  isSingleEntryBlock() {
    return this.rowTranslation.length === 1 && this.colTranslation.length === 1;
  }

  updateNodeId(nodeMap) {
    Object.entries(this.models).forEach(([modelId, insertion]) => {
      if (nodeMap[modelId]) {
        this.occupyBlocks(nodeMap[modelId], insertion);
        delete this.models[modelId];
      }
    });
  }

  toJson() {
    const blockData = {
      referenceTransform: this.transform.toArray(),
      parentTransform: this.parentTransform.toArray(),
      colWidths: this.colWidths,
      rowHeights: this.rowHeights,
      options: {
        occupyRow: this.occupyRow,
        occupyCol: this.occupyCol,
        withRotation: this.withRotation,
        marginLeft: this.marginLeft,
        marginRight: this.marginRight,
        marginTop: this.marginTop,
        marginBottom: this.marginBottom,
      },
      models: this.models,
    };
    return JSON.stringify(blockData);
  }

  fromJson(json) {
    let blockData;
    try {
      blockData = JSON.parse(json);
    } catch (e) {
      throw new Error(e);
    }
    const { Matrix4 } = this.THREE;
    const referenceTransform = new Matrix4().fromArray(
      blockData.referenceTransform
    );
    const parentTransform = new Matrix4().fromArray(blockData.parentTransform);
    this._initBlockLayoutData(
      referenceTransform,
      parentTransform,
      blockData.rowHeights,
      blockData.colWidths,
      blockData.options
    );
    this.blocks = this._createBlocks();
    this.models = blockData.models;
    Object.entries(this.models).forEach(([modelId, insertion]) =>
      this.occupyBlocks(modelId, insertion)
    );
  }

  toString() {
    let str = '';
    for (let row of this.blocks) {
      for (let block of row) {
        str += block + '\t';
      }
      str += '\n';
    }
    return str;
  }
}

export const getBlockFromModelId = (blocks, modelId) => {
  if (!modelId) return;
  return blocks.find((block) => block.containNode(modelId));
};



// WEBPACK FOOTER //
// ./src/utils/BlockLayout.js